// checkRiskGate.js
'use strict';
import fetchRiskPreflight from '../action/fetchRiskPreflight.js';
import submitRiskCheckSync from '../action/submitRiskCheckSync.js';
import getFpIdPro from '../action/getFpIdPro.js';
import getOperationData from '../selector/getOperationData.js';
import { MERGE_OPERATION_DATA } from '../ActionTypes.js';

const BLOCKED_ERROR_KEY = 'error_fpid_ineligible';

/**
 * Risk gate. Writes { status, errorKey } to operation['riskGate']. Used by
 * checkout and iframe pages. status mirrors the backend vocabulary: 'pass' or
 * 'blocked' (any non-pass outcome — backend blocked, or a 4xx/timeout error —
 * maps to 'blocked' and stops the flow).
 *
 *   preflight ─┬─ 'pass'     ─────────────────────► status 'pass'
 *              ├─ 'blocked'  ─────────────────────► status 'blocked' (blocked errorKey)
 *              ├─ 'error'    ─────────────────────► status 'blocked' (4xx errorKey)
 *              └─ 'required' ─► risk-check ─┬─ 'pass'    ──► status 'pass'
 *                                           ├─ 'blocked' ──► status 'blocked' (blocked errorKey)
 *                                           └─ 'error'   ──► status 'blocked' (4xx errorKey)
 *
 * @kind action
 * @param {string} {triggeredType} - risk-check scene tag, e.g. 'STORE'.
 * @return {Promise} Action promise.
 */
const checkRiskGate =
  ({ triggeredType }) =>
  async (dispatch, getState) => {
    let status = 'pass';
    let errorKey = '';

    // Step 1: preflight — read-only, no FPJS cost, asks if this user needs verifying.
    await dispatch(fetchRiskPreflight());
    const preflightState = getOperationData(
      getState(),
      ['fingerprint'],
      'preflightState'
    );

    if (preflightState === 'blocked') {
      status = 'blocked';
      errorKey = BLOCKED_ERROR_KEY;
    } else if (preflightState === 'error') {
      status = 'blocked';
      errorKey = getOperationData(
        getState(),
        ['fingerprint'],
        'preflightErrorKey'
      );
    } else if (preflightState === 'required') {
      // Step 2: risk-check — real verification (costs 1 FPJS get), only when required.
      await dispatch(submitRiskCheckSync({ triggeredType }));
      const riskCheckState = getOperationData(
        getState(),
        ['fingerprint'],
        'riskCheckState'
      );
      if (riskCheckState === 'blocked') {
        status = 'blocked';
        errorKey = BLOCKED_ERROR_KEY;
      } else if (riskCheckState === 'error') {
        status = 'blocked';
        errorKey = getOperationData(
          getState(),
          ['fingerprint'],
          'riskCheckErrorKey'
        );
      }
    }

    // Step 3: get the FingerprintJS visitorId (a stable per-device id).
    // Run this for both pass and blocked results, because the backend needs
    // the device id in every case to tell repeated requests from the same
    // device apart. getFpIdPro reads the cached id from IndexedDB and only
    // calls the FingerprintJS API when there is none. It also sets the global
    // X-Fingerprint-Pro-Id header (so later API calls send the id) and saves
    // the id back to IndexedDB (so the iframe can read it).
    await dispatch(getFpIdPro({ via: 'risk_gate' }));

    return dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['riskGate'],
        data: { status, errorKey: errorKey || '' },
      },
    });
  };

export default checkRiskGate;
